import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Input, FormGroup, Label, Form, Row, Col, Card, CardBody } from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const EditFarmer = () => {
    const { id } = useParams(); 
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // 🔥 Form States - Updated to match structure, including 'type' and 'status'
    const [formData, setFormData] = useState({
        id: id,
        name: "",
        phone: "",
        type: "",    // 'farmer' or 'trader'
        status: "",  // '1' or '0'
        address: "",
        acc_no: "",
        ifsc_code: ""
    });

    const [errors, setErrors] = useState({});

    // Existing data-va fetch panni form-la fill panna
    const getFarmerDetails = async () => {
        dispatch(loadingTrue());
        try {
            const res = await Handler({ method: "GET", url: `/farmer/view.php?id=${id}` });
            if (res?.success) {
                const d = res.data;
                // 🔥 Machan, fixed mapping bugs by accessing the nested 'contact', 'bank' and 'status' properties from API
                setFormData({
                    id: d.id,
                    name: d.contact?.name || "",
                    phone: d.contact?.phone || "",
                    type: d.contact?.type || "",
                    status: d.status?.value !== undefined ? String(d.status.value) : "1",
                    address: d.address || "",
                    acc_no: d.bank?.account_no || "",
                    ifsc_code: d.bank?.ifsc_code || ""
                });
            } else {
                toast.error(res?.message || "Record not found");
                navigate("/farmers");
            }
        } catch (err) {
            toast.error("Error fetching record details");
        } finally {
            dispatch(loadingFalse());
        }
    };

    useEffect(() => {
        if (id) getFarmerDetails();
    }, [id]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (errors[e.target.name]) {
            setErrors({ ...errors, [e.target.name]: "" });
        }
    };

    // 🔥 Frontend Validation Logic Updated to match strict API update requirements
    const validateForm = () => {
        let formErrors = {};
        let isValid = true;

        // Name Validation
        if (!formData.name.trim()) {
            formErrors.name = "Name is required";
            isValid = false;
        }

        // Phone Validation (Strict 10 digit check)
        if (!formData.phone.trim()) {
            formErrors.phone = "Phone number is required";
            isValid = false;
        } else if (!/^[0-9]{10}$/.test(formData.phone.trim())) {
            formErrors.phone = "Invalid phone number (10 digits required)";
            isValid = false;
        }

        // Type Validation
        if (!formData.type) {
            formErrors.type = "Type selection is required";
            isValid = false;
        }

        // IFSC Validation (Strictly matches the PHP regex condition)
        if (formData.ifsc_code.trim()) {
            const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
            if (!ifscRegex.test(formData.ifsc_code.trim().toUpperCase())) {
                formErrors.ifsc_code = "Invalid IFSC format (e.g., SBIN0001234)";
                isValid = false;
            }
        }

        // Account Number Validation
        if (formData.acc_no.trim() && formData.acc_no.trim().length < 6) {
            formErrors.acc_no = "Invalid account number (min 6 digits)";
            isValid = false;
        }

        setErrors(formErrors);

        if (!isValid) {
            toast.error("Please fill required fields correctly!", {
                toastId: "val-err"
            });
        }

        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        dispatch(loadingTrue());
        setErrors({});

        // Payload formatted strictly for backend array expectations
        const payload = {
            id: parseInt(formData.id),
            name: formData.name.trim(),
            phone: formData.phone.trim(),
            type: formData.type,
            status: parseInt(formData.status),
            address: formData.address.trim(),
            acc_no: formData.acc_no.trim(),
            ifsc_code: formData.ifsc_code.trim().toUpperCase()
        };

        try {
            const res = await Handler({
                method: "POST", // Method fallback as allowed inside api options check
                url: "/farmer/update.php",
                data: payload
            });

            if (res?.success) {
                const labelText = formData.type === 'farmer' ? 'Farmer' : 'Trader';
                Swal.fire("Updated!", `${labelText} details updated successfully`, "success");
                navigate("/farmers");
            } else {
                if (res?.errors) {
                    setErrors(res.errors);
                } else {
                    toast.error(res?.message || "Update failed");
                }
            }
        } catch (err) {
            Swal.fire("Error", "Server error occurred", "error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <div className="page-container p-1">
            <div className="page-header mb-4">
                <h6 className="text-muted">Management &gt; Profiles &gt; Edit</h6>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="fw-bold">Modify Profile Records</h3>
                        <p className="text-muted">Update info for Registered Profile ID: #{id}</p>
                    </div>
                    <Button color="secondary" outline onClick={() => navigate(-1)}>
                        <i className="bx bx-arrow-back"></i> Back
                    </Button>
                </div>
            </div>

            <Card className="border-0 shadow-sm" style={{ borderRadius: '15px' }}>
                <CardBody className="p-4">
                    <Form onSubmit={handleSubmit}>
                        {/* Basic Info Rows */}
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="name">Full Name *</Label>
                                    <Input
                                        type="text"
                                        name="name"
                                        id="name"
                                        placeholder="Enter name"
                                        className={errors.name ? "is-invalid" : ""}
                                        value={formData.name}
                                        onChange={handleChange}
                                    />
                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="phone">Phone Number *</Label>
                                    <Input
                                        type="text"
                                        name="phone"
                                        id="phone"
                                        maxLength="10"
                                        placeholder="10 digit number"
                                        className={errors.phone ? "is-invalid" : ""}
                                        value={formData.phone}
                                        onChange={handleChange}
                                    />
                                    {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                                </FormGroup>
                            </Col>
                        </Row>

                        {/* 🔥 Added Type Dropdown and Status Switches inside dynamic Grid columns */}
                        <Row className="mt-3">
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="type">Profile Type *</Label>
                                    <Input
                                        type="select"
                                        name="type"
                                        id="type"
                                        className={errors.type ? "is-invalid" : ""}
                                        value={formData.type}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Type</option>
                                        <option value="farmer">Farmer</option>
                                        <option value="trader">Trader</option>
                                    </Input>
                                    {errors.type && <div className="invalid-feedback">{errors.type}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="status">Account Status *</Label>
                                    <Input
                                        type="select"
                                        name="status"
                                        id="status"
                                        className={errors.status ? "is-invalid" : ""}
                                        value={formData.status}
                                        onChange={handleChange}
                                    >
                                        <option value="1">Active</option>
                                        <option value="0">Inactive</option>
                                    </Input>
                                    {errors.status && <div className="invalid-feedback">{errors.status}</div>}
                                </FormGroup>
                            </Col>
                        </Row>

                        {/* Bank Info Addons */}
                        <Row className="mt-3">
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="acc_no">Bank Account Number</Label>
                                    <Input
                                        type="text"
                                        name="acc_no"
                                        id="acc_no"
                                        placeholder="Enter account number"
                                        className={errors.acc_no ? "is-invalid" : ""}
                                        value={formData.acc_no}
                                        onChange={handleChange}
                                    />
                                    {errors.acc_no && <div className="invalid-feedback">{errors.acc_no}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="ifsc_code">IFSC Code</Label>
                                    <Input
                                        type="text"
                                        name="ifsc_code"
                                        id="ifsc_code"
                                        placeholder="e.g., SBIN0001234"
                                        maxLength="11"
                                        className={errors.ifsc_code ? "is-invalid" : ""}
                                        value={formData.ifsc_code}
                                        onChange={handleChange}
                                    />
                                    {errors.ifsc_code && <div className="invalid-feedback">{errors.ifsc_code}</div>}
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col md={12}>
                                <FormGroup>
                                    <Label for="address">Address Details</Label>
                                    <Input
                                        type="textarea"
                                        name="address"
                                        id="address"
                                        placeholder="Enter full location details"
                                        rows="3"
                                        value={formData.address}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className="mt-4 pt-3 border-top d-flex justify-content-end">
                            <Button color="light" className="me-2" onClick={() => navigate(-1)}>
                                Cancel
                            </Button>
                            <Button type="submit" className="add-btn px-5" color="primary">
                                <i className="bx bx-save me-1"></i> Update Profile
                            </Button>
                        </div>
                    </Form>
                </CardBody>
            </Card>
        </div>
    );
};

export default EditFarmer;